import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [valid, setValid] = useState(null); // null=checking, true|false=resolved
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState({ new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/auth/reset-password/${token}`)
      .then((r) => {
        setValid(true);
        setEmail(r.data.email);
      })
      .catch((err) => {
        setValid(false);
        setErrorMsg(
          formatApiErrorDetail(err.response?.data?.detail) ||
            "Lenken er ugyldig eller har utløpt.",
        );
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) {
      toast.error("Passordene stemmer ikke overens");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: pw.new_password,
      });
      toast.success("Passord oppdatert — logg inn med ditt nye passord");
      nav("/logg-inn");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (valid === null) {
    return (
      <div className="tk-container py-20 text-slate-500" data-testid="reset-loading">
        Validerer lenken …
      </div>
    );
  }

  if (valid === false) {
    return (
      <div className="tk-container py-20 max-w-md text-center" data-testid="reset-invalid">
        <div className="w-14 h-14 rounded-full bg-amber-50 mx-auto mb-6 flex items-center justify-center">
          <AlertTriangle className="text-amber-600" size={26} />
        </div>
        <h1 className="text-3xl font-semibold mb-3">Lenken er ikke gyldig</h1>
        <p className="text-slate-600 mb-8">{errorMsg}</p>
        <Link to="/glemt-passord" className="tk-btn-primary" data-testid="request-new-link">
          Be om ny lenke
        </Link>
      </div>
    );
  }

  return (
    <div className="tk-container py-20 max-w-md" data-testid="reset-page">
      <p className="tk-overline mb-3">Konto</p>
      <h1 className="text-3xl font-semibold mb-2">Sett nytt passord</h1>
      <p className="text-sm text-slate-500 mb-8">
        For <strong>{email}</strong>. Velg et nytt passord på minst 8 tegn.
      </p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-sm font-medium text-slate-700">Nytt passord</label>
          <input
            type="password"
            required
            minLength={8}
            className="tk-input mt-1"
            value={pw.new_password}
            onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
            data-testid="reset-new-pw"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Bekreft nytt passord</label>
          <input
            type="password"
            required
            className="tk-input mt-1"
            value={pw.confirm}
            onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
            data-testid="reset-confirm-pw"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="tk-btn-primary w-full justify-center"
          data-testid="reset-submit"
        >
          {busy ? "Lagrer …" : "Oppdater passord"}
        </button>
      </form>
    </div>
  );
}
